import { ONE_DAY_IN_MS, QUARTER_HOUR_IN_MS } from '../constants/constants.ts';
import { STORE_CONSTANTS } from '../enums/enums.ts';
import { AuthResponse, User } from '../types/Types';
import bcrypt from 'bcryptjs';

class MockAPI {
  private users: User[] = [];
  private tokens: { [key: string]: { userId: number; expiresAt: number } } = {};
  private nextId: number;

  constructor() {
    this.loadUsersFromStorage();
    this.loadTokensFromStorage();
    this.loadNextIdFromStorage();
  }

  private loadUsersFromStorage() {
    const storedUsers = localStorage.getItem(STORE_CONSTANTS.MOCK_API_USERS);

    if (storedUsers) {
      this.users = JSON.parse(storedUsers);
    }
  }

  private loadTokensFromStorage() {
    const storedTokens = localStorage.getItem(STORE_CONSTANTS.MOCK_API_TOKENS);

    if (storedTokens) {
      this.tokens = JSON.parse(storedTokens);
    }
  }

  private loadNextIdFromStorage() {
    const nextId = localStorage.getItem(STORE_CONSTANTS.NEXT_ID);

    if (nextId) {
      this.nextId = parseInt(nextId, 10);
    } else {
      const highestId = this.users.length > 0 ? Math.max(...this.users.map((u) => u.id)) : 0;

      this.nextId = highestId + 1;
      this.saveNextIdToStorage();
    }
  }

  private saveUsersToStorage() {
    localStorage.setItem(STORE_CONSTANTS.MOCK_API_USERS, JSON.stringify(this.users));
  }

  private saveTokensToStorage() {
    localStorage.setItem(STORE_CONSTANTS.MOCK_API_TOKENS, JSON.stringify(this.tokens));
  }

  private saveNextIdToStorage() {
    localStorage.setItem(STORE_CONSTANTS.NEXT_ID, this.nextId.toString());
  }

  private getAllSavedEmails(): Set<string> {
    return new Set(this.users.map((user) => user.email));
  }

  private getAllSavedUsernames(): Set<string> {
    return new Set(this.users.map((user) => user.username));
  }

  public checkIfUsernameExists(userName: string): boolean {
    return this.getAllSavedUsernames().has(userName);
  }

  public checkIfEmailExists(email: string): boolean {
    return this.getAllSavedEmails().has(email);
  }

  private generateId(): number {
    const id = this.nextId;
    this.nextId++;
    this.saveNextIdToStorage();

    return id;
  }

  public register(
    firstName: string,
    lastName: string,
    username: string,
    email: string,
    password: string,
  ): User {
    if (this.checkIfEmailExists(email) || this.checkIfUsernameExists(username)) {
      throw new Error('Username or Email already exists');
    }

    const salt = bcrypt.genSaltSync(10);
    const hashedPassword = bcrypt.hashSync(password, salt);

    const newUser: User = {
      id: this.generateId(),
      firstName,
      lastName,
      username,
      email,
      password: hashedPassword,
    };
    this.users.push(newUser);
    this.saveUsersToStorage();

    return newUser;
  }

  public login(usernameOrEmail: string, password: string): AuthResponse {
    const user = this.users.find(
      (u) => u.email === usernameOrEmail || u.username === usernameOrEmail,
    );

    if (!user || !bcrypt.compareSync(password, user.password)) {
      throw new Error('Invalid credentials');
    }
    const accessToken = this.generateToken(user.id);
    const refreshToken = this.generateToken(user.id, true);

    return { user, accessToken, refreshToken };
  }

  public refreshToken(token: string): AuthResponse {
    const userId = this.verifyToken(token);
    if (!userId) {
      throw new Error('Invalid refresh token');
    }

    const user = this.users.find((u) => u.id === userId);
    if (!user) {
      throw new Error('User not found');
    }

    const accessToken = this.generateToken(user.id);
    const refreshToken = this.generateToken(user.id, true);
    return { user, accessToken, refreshToken };
  }

  private generateToken(userId: number, isRefreshToken: boolean = false): string {
    const token = Math.random().toString(36).slice(2);
    const expiresIn = isRefreshToken ? ONE_DAY_IN_MS : QUARTER_HOUR_IN_MS;
    const expiresAt = Date.now() + expiresIn;
    this.tokens[token] = { userId, expiresAt };
    this.saveTokensToStorage();
    return token;
  }

  private verifyToken(token: string): number | null {
    const tokenData = this.tokens[token];
    if (!tokenData) {
      return null;
    }
    const { userId, expiresAt } = tokenData;
    if (Date.now() > expiresAt) {
      delete this.tokens[token];
      this.saveTokensToStorage();
      return null;
    }
    return userId;
  }
}

export const mockAPI = new MockAPI();
